import { AskRequest, EntityLink, ResolvedLink } from '@hone-automation/common';
import { IconBlockerAlert, IconChevron } from 'components/Icons';
import React, { useState } from 'react';

import './TransactionDetails.scss';
import { prefixClass } from '../../../../lib/utils';
import { StatusItem } from '../common/StatusItem';
import { RequestStatus } from '../RequestItem/type';
import classNames from 'classnames';
import { format } from 'date-fns';
import { formatAmount } from 'presentation/components/NewTable/NTCell/ValueFormatters/ValueFormatters';
import { Tooltip, Whisper } from 'rsuite';
import useFetchLinks from 'hooks/useFetchLinks';
import { useParams } from 'react-router-dom';
import { useTransactionDetails, EnhancedAskRequest, mapAskRequestTypeToLabel } from 'hooks/useTransactionDetails';

interface TransactionDetailsProps {
  request: EnhancedAskRequest;
  children?: React.ReactNode;
}

export const STATUS_MAP: { [key: string]: RequestStatus } = {
  created: 'created',
  Open: 'created',
  'Information Needed': 'created',
  'In Progress': 'inProgress',
  Closed: 'resolved',
  Categorized: 'resolved',
  inProgress: 'inProgress',
  resolved: 'resolved',
};

const TRXDetailsItem: React.FC<{
  label: string;
  value?: string | number;
  money?: boolean;
  originalMoney?: number;
  highlight?: boolean;
}> = ({ label, value, money, originalMoney, highlight }) => {
  if (!value || value === '-') return null;
  const prefix = prefixClass('transaction-info-details-prop');
  const valueNum = typeof value === 'string' ? parseFloat(value) : value;

  const renderValue = () => {
    if (money) {
      return (
        <>
          {formatAmount(valueNum, 2, true)}
          {(originalMoney || originalMoney === 0) && originalMoney !== valueNum && (
            <span className={prefix('original-money')}>/ {formatAmount(originalMoney, 2, true)}</span>
          )}
        </>
      );
    }
    return <>{value}</>;
  };
  return (
    <div className={classNames(prefix(), { alert: highlight })}>
      <div className={prefix('label')}>{label}</div>
      <div className={classNames(prefix('amount'), { money })}>{renderValue()}</div>
    </div>
  );
};

// Component for collapsed view
const CollapsedView: React.FC<{
  request: AskRequest;
  prefix: (modifier?: string) => string;
  isCustomRequest: boolean;
}> = ({ request, prefix, isCustomRequest }) => {
  const trxName = request.transaction?.name || '-';
  const amount = request.transaction?.amount || '-';
  const description = isCustomRequest
    ? request.identifier || `Flagged Cell `
    : trxName || request.transaction?.memo || '-';
  return (
    <div className={prefix('collapsed-section')}>
      <div className={prefix('collapsed-description')}>{description}</div>
    </div>
  );
};

// Component for Custom Requests and Flagged Cells
const SimpleRequestView: React.FC<{
  request: AskRequest;
  isCustomRequest: boolean;
  isFlaggedCell: boolean;
  formattedDates: { createdDate: string; trxDate: string };
  createdByUser: string;
  linkAmount?: number;
  sectionBreadcrumb?: string | null;
  hasBlockerPriority?: boolean;
}> = ({
  request,
  isCustomRequest,
  isFlaggedCell,
  formattedDates,
  createdByUser,
  linkAmount,
  sectionBreadcrumb,
  hasBlockerPriority,
}) => {
  const { createdDate } = formattedDates;
  const amount = request.transaction?.amount || '-';
  const requestType = mapAskRequestTypeToLabel[request.type];

  if (isCustomRequest) {
    return (
      <>
        <TRXDetailsItem label="Type" value={requestType} />
        <TRXDetailsItem label="Title" value={request.identifier} />
        <TRXDetailsItem label="Created on" value={createdDate} />
        <TRXDetailsItem label="Created by" value={createdByUser} />

        {/* @ts-ignore */}
        {request.priority === 'Blocker' && <TRXDetailsItem highlight label="Priority" value="Urgent" />}
      </>
    );
  }

  if (isFlaggedCell) {
    // Logic for Current Amount and Original Amount
    // If sectionAmount (linkAmount) exists and differs from flaggedAmount:

    const flaggedAmountValue = request.flaggedAmount ?? (typeof amount === 'number' ? amount : undefined);
    const hasSectionAmount = linkAmount !== undefined && linkAmount !== null;
    const amountsAreDifferent = hasSectionAmount && linkAmount !== flaggedAmountValue;

    const currentAmount = amountsAreDifferent ? linkAmount : flaggedAmountValue;
    const originalAmount = amountsAreDifferent ? flaggedAmountValue : undefined;

    return (
      <>
        <TRXDetailsItem label="Type" value={'Flagged Report Cell'} />
        <TRXDetailsItem label="Created on" value={createdDate} />
        <TRXDetailsItem label="Created by" value={createdByUser} />
        <TRXDetailsItem label="Current Amount" value={currentAmount} money originalMoney={originalAmount} />

        {/* @ts-ignore */}
        {request.priority === 'Blocker' && <TRXDetailsItem highlight label="Priority" value="Urgent" />}
      </>
    );
  }

  return null;
};

// Component for Flagged Transactions and Ask Requests
const TransactionRequestView: React.FC<{
  request: AskRequest;
  isFlaggedTransaction: boolean;
  isAskRequest: boolean;
  formattedDates: { createdDate: string; trxDate: string };
  createdByUser: string;
  hasBlockerPriority?: boolean;
  sectionBreadcrumb?: string | null;
}> = ({
  request,
  isFlaggedTransaction,
  isAskRequest,
  formattedDates,
  createdByUser,
  hasBlockerPriority,
  sectionBreadcrumb,
}) => {
  if (!isFlaggedTransaction && !isAskRequest) return null;

  const { createdDate, trxDate } = formattedDates;
  const requestType = mapAskRequestTypeToLabel[request.type];
  const accountInfo = request.transaction?.account || '--';
  const trxName = request.transaction?.name || 'No Vendor Name';
  const description = request.transaction?.memo || '-';
  const amount = request.transaction?.amount || '-';

  return (
    <>
      <TRXDetailsItem label="Type" value={requestType} />
      <TRXDetailsItem label="Original Account" value={accountInfo} />
      {sectionBreadcrumb && <TRXDetailsItem label="Section" value={sectionBreadcrumb} />}
      <TRXDetailsItem label="Final Account" value={request?.resolution} />
      <TRXDetailsItem label="Created on" value={createdDate} />
      <TRXDetailsItem label="Created by" value={createdByUser} />
      <TRXDetailsItem label="Transaction Date" value={trxDate} />
      <TRXDetailsItem label="Name" value={trxName} />
      <TRXDetailsItem label="Description" value={description} />
      <TRXDetailsItem label="Amount" value={request.flaggedAmount || amount} money />

      {hasBlockerPriority && <TRXDetailsItem highlight label="Priority" value="Urgent" />}
    </>
  );
};

const FlowInfoItem = ({ status, date }: { status: string; date: string }) => {
  const prefix = prefixClass('transaction-info-details-flow-item');
  return (
    <div className={prefix()}>
      <div className={prefix('flow-item-status')}>{status}</div>
      <div className={prefix('flow-item-date')}>{date}</div>
    </div>
  );
};

const TransactionsFlow = ({ request }: { request: AskRequest }) => {
  const prefix = prefixClass('transaction-info-details-flow');
  const { status, comments } = request;
  const createdDate = request.createdAt ? format(new Date(request.createdAt), 'MM/dd/yyyy') : '-';
  const createdByUser =
    request.comments?.[0]?.user?.name ||
    request.comments?.[0]?.userLocation?.email?.split('@')[0] ||
    request.comments?.[0]?.userLocation?.role ||
    'Unknown';
  //find the latest status
  const latestStatus = comments
    ?.slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const updatedDate = latestStatus?.createdAt ? format(new Date(latestStatus.createdAt), 'MM/dd/yyyy') : '-';

  const updatedByUser =
    latestStatus?.user?.name ||
    latestStatus?.userLocation?.email?.split('@')[0] ||
    latestStatus?.userLocation?.role ||
    'Unknown';
  return (
    <div className={prefix()}>
      <div className={prefix('header')}>
        <div className={prefix('header-label')}>Flow</div>
      </div>
      <div className={prefix('items')}>
        {status === 'Closed' && (
          <>
            <div className={prefix('item')}>
              <StatusItem userName={updatedByUser} status={STATUS_MAP['Closed']} date={updatedDate} showDate />
            </div>
          </>
        )}
        {status === 'In Progress' && (
          <div className={prefix('item')}>
            <StatusItem userName={updatedByUser} status={STATUS_MAP['In Progress']} date={updatedDate} showDate />
          </div>
        )}
        <StatusItem userName={createdByUser} status={STATUS_MAP['Open']} date={createdDate} showDate />
      </div>
    </div>
  );
};

const TransactionDetails: React.FC<TransactionDetailsProps> = ({ request, children }) => {
  const prefix = prefixClass('transaction-info-details');

  const [isCollapsed, setIsCollapsed] = useState(false);
  const { locationId } = useParams();
  const { data: links } = useFetchLinks(request, locationId);
  const [infoVisble, setInfoVisble] = useState(false);

  // Use the transaction details hook to get all formatted data
  const details = useTransactionDetails(request);

  // Extract account information from links
  const linkAmount = links ? (links as ResolvedLink<EntityLink>[])[0]?.sectionAmount : undefined;
  const sectionBreadcrumb =
    links && (links as ResolvedLink<EntityLink>[])?.length > 0
      ? (links as ResolvedLink<EntityLink>[])[0]?.sectionPath?.replaceAll('|', ' > ')
      : undefined;

  // Extract values from the hook or fallback to defaults
  const {
    requestType = '',
    isAskRequest = false,
    isFlaggedTransaction = false,
    isFlaggedCell = false,
    isCustomRequest = false,
    createdDate = '-',
    trxDate = '-',
    createdByUser = 'Unknown',
    hasBlockerPriority = false,
  } = details || {};

  return (
    <div className={classNames(prefix(), { 'not-visible': !children || React.Children.count(children) === 0 })}>
      {infoVisble && (
        <div className={prefix('container')}>
          <button
            className={classNames(prefix('collapse-btn'), { active: !isCollapsed })}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <IconChevron />
          </button>

          {!isCollapsed ? (
            <>
              <div className={prefix('props')}>
                <div className={prefix('props-header')}>
                  <div className={prefix('props-header-label')}>Context</div>
                </div>
                {(isCustomRequest || isFlaggedCell) && (
                  <SimpleRequestView
                    request={request}
                    isCustomRequest={isCustomRequest}
                    isFlaggedCell={isFlaggedCell}
                    formattedDates={{ createdDate, trxDate }}
                    createdByUser={createdByUser}
                    linkAmount={linkAmount}
                    hasBlockerPriority={hasBlockerPriority}
                    sectionBreadcrumb={sectionBreadcrumb}
                  />
                )}

                {(isFlaggedTransaction || isAskRequest) && (
                  <TransactionRequestView
                    request={request}
                    isFlaggedTransaction={isFlaggedTransaction}
                    isAskRequest={isAskRequest}
                    formattedDates={{ createdDate, trxDate }}
                    createdByUser={createdByUser}
                    hasBlockerPriority={hasBlockerPriority}
                    sectionBreadcrumb={sectionBreadcrumb}
                  />
                )}
              </div>
              <TransactionsFlow request={request} />
            </>
          ) : (
            <>
              <CollapsedView request={request} isCustomRequest prefix={prefix} />
              {hasBlockerPriority && (
                <Whisper trigger="hover" placement="top" speaker={<Tooltip>Urgent Request</Tooltip>}>
                  <div className={prefix('collapsed-sign')}>
                    <IconBlockerAlert />
                  </div>
                </Whisper>
              )}
              <StatusItem userName={''} status={STATUS_MAP[request.status]} date={createdDate} showDate />
            </>
          )}
        </div>
      )}
      <div className={prefix('actions')}>{children}</div>
    </div>
  );
};

export default TransactionDetails;
